import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { metricsService } from '../../services/endpoints/metricsService';
import { format } from 'date-fns';

const TODAY = format(new Date(), 'yyyy-MM-dd');

const WELLBEING_SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const QuickCheckIn: React.FC = () => {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  const [weight, setWeight]         = useState<string>('');
  const [sleepHours, setSleepHours] = useState<string>('');
  const [steps, setSteps]           = useState<string>('');
  const [wellbeing, setWellbeing]   = useState<number | null>(null);

  // Pre-fill if today's entry already exists
  const { data: existing } = useQuery({
    queryKey: ['metrics', TODAY],
    queryFn: () => metricsService.getMetricsByDate(TODAY),
    retry: false,
    staleTime: 60_000,
  });

  React.useEffect(() => {
    if (existing) {
      if (existing.weight)           setWeight(String(existing.weight));
      if (existing.sleepHours)       setSleepHours(String(existing.sleepHours));
      if (existing.stepsYesterday)   setSteps(String(existing.stepsYesterday));
      if (existing.wellbeingScore)   setWellbeing(existing.wellbeingScore);
    }
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: metricsService.saveMetrics,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['metrics-today-check'] }); // clears check-in reminder badge
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const handleSave = () => {
    const payload: any = {
      ...existing,
      date:             TODAY,
      weight:           weight     ? parseFloat(weight)     : existing?.weight,
      sleepHours:       sleepHours ? parseFloat(sleepHours) : existing?.sleepHours,
      stepsYesterday:   steps      ? parseInt(steps, 10)    : existing?.stepsYesterday,
      wellbeingScore:   wellbeing  ?? existing?.wellbeingScore,
    };
    saveMutation.mutate(payload);
  };

  const hasInput = weight || sleepHours || steps || wellbeing !== null;

  return (
    <div className="px-8 py-4">
      <div className="bg-dark-800 border border-dark-700 rounded-xl p-5">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-base font-semibold text-white">⚡ Quick Check-In</h2>
            <p className="text-xs text-gray-400 mt-0.5">{format(new Date(), 'EEEE, MMMM d')}</p>
          </div>
          <a
            href="/metrics/entry"
            className="text-xs text-gold-400 hover:text-gold-300 underline-offset-2 hover:underline"
          >
            Full Check-In →
          </a>
        </div>

        {/* Numeric fields: 3-column grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {/* Morning Weight */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Morning Weight (lbs)</label>
            <input
              type="number"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-3 py-2 text-sm placeholder-gray-600 focus:border-gold-500 focus:outline-none"
              placeholder="182.0"
            />
          </div>

          {/* Sleep Hours */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Sleep (hrs)</label>
            <input
              type="number"
              step="0.5"
              min="0"
              max="24"
              value={sleepHours}
              onChange={(e) => setSleepHours(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-3 py-2 text-sm placeholder-gray-600 focus:border-gold-500 focus:outline-none"
              placeholder="7.5"
            />
          </div>

          {/* Steps Yesterday */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Steps (yesterday)</label>
            <input
              type="number"
              step="1"
              min="0"
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 text-white rounded-lg px-3 py-2 text-sm placeholder-gray-600 focus:border-gold-500 focus:outline-none"
              placeholder="8500"
            />
          </div>
        </div>

        {/* Wellbeing tap row */}
        <div className="mb-4">
          <label className="block text-xs text-gray-400 mb-2">Wellbeing</label>
          <div className="flex items-center gap-1">
            <span className="text-base mr-1" title="Feeling terrible">😫</span>
            {WELLBEING_SCORES.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setWellbeing(wellbeing === n ? null : n)}
                className={`w-7 h-7 rounded-md text-xs font-semibold transition-colors ${
                  wellbeing === n
                    ? 'bg-gold-500 text-black'
                    : 'bg-dark-700 border border-dark-600 text-gray-400 hover:border-gold-600 hover:text-white'
                }`}
              >
                {n}
              </button>
            ))}
            <span className="text-base ml-1" title="Feeling perfect">🔥</span>
          </div>
        </div>

        {/* Save button + status */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={!hasInput || saveMutation.isPending}
            className="px-5 py-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-40 text-black font-semibold text-sm rounded-lg transition-colors"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Check-In'}
          </button>
          {saved && (
            <span className="text-green-400 text-sm flex items-center gap-1">
              <span>✅</span> Saved!
            </span>
          )}
          {saveMutation.isError && (
            <span className="text-red-400 text-sm">Failed to save. Try again.</span>
          )}
        </div>

      </div>
    </div>
  );
};

export default QuickCheckIn;
